import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { SrIcon } from "@/components/ui/SrIcon";
import { useFollowUpRuleThresholds } from "@/hooks/use-crm-config";
import { useAllSystemConfig } from "@/hooks/use-system-config";
import {
  FOLLOW_UP_RULES,
  FOLLOW_UP_RULE_KEYS,
  formatRuleThreshold,
} from "@/lib/follow-up-rules";

const RULES_KEY = "crm_follow_up_rules";
// Stilguiden redigeres i CrmStyleGuidePanel, ikke som rå verdi her.
const HIDDEN_KEYS = new Set([RULES_KEY, "crm_style_guide"]);

function formatConfigValue(value: unknown): string {
  if (value === null || value === undefined) return "–";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return "–";
  }
}

/**
 * Drift → CRM: oppfølgingsreglene med gjeldende terskler (read-only), pluss
 * øvrige crm_*-innstillinger. Terskler endres i system_config av superadmin.
 */
export function CrmRulesPanel() {
  const { data: thresholds, isLoading } = useFollowUpRuleThresholds();
  const { data: configRows } = useAllSystemConfig();
  const otherRows = (configRows ?? []).filter((r) => r.key.startsWith("crm_") && !HIDDEN_KEYS.has(r.key));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <SrIcon name="target" className="h-4 w-4 text-muted-foreground" />
          Oppfølgingsregler
        </CardTitle>
        <CardDescription>
          Reglene kjøres hver hverdag morgen og lager oppfølginger i køen til Salgsmuligheter. Maks én åpen per prosess
          per regel. Tersklene ligger i system_config og vises her uten redigering.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {isLoading ? (
          <SrIcon name="loader2" className="w-4 h-4 animate-spin text-muted-foreground" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" aria-label="Oppfølgingsregler">
              <thead>
                <tr>
                  <th className="text-left p-2 font-medium">Regel</th>
                  <th className="text-left p-2 font-medium">Når</th>
                  <th className="text-left p-2 font-medium">Terskel</th>
                </tr>
              </thead>
              <tbody>
                {FOLLOW_UP_RULE_KEYS.map((key) => (
                  <tr key={key} className="border-t border-border/50">
                    <td className="p-2 font-medium">{FOLLOW_UP_RULES[key].label}</td>
                    <td className="p-2 text-muted-foreground">{FOLLOW_UP_RULES[key].description}</td>
                    <td className="p-2 whitespace-nowrap">{formatRuleThreshold(key, thresholds)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {otherRows.length > 0 && (
          <div>
            <h3 className="text-sm font-medium mb-2">Øvrige CRM-innstillinger</h3>
            <table className="w-full text-sm" aria-label="Øvrige CRM-innstillinger">
              <tbody>
                {otherRows.map((row) => (
                  <tr key={row.key} className="border-t border-border/50">
                    <td className="p-2 font-mono text-xs">{row.key}</td>
                    <td className="p-2">{formatConfigValue(row.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
